// Angular
import { Injectable } from '@angular/core';

// Custom
import { environment } from 'src/environments/environment';
import { FormioForm } from '@formio/angular';
import { ItemWorkflowFile } from '../interfaces/items/item-workflow-file';
import { ItemWorkflowFileType } from '../enum/item/item-workflow-file-type.enum';
import { ItemWorkflowFilePrefixes } from '../enum/item/item-workflow-file-prefixes.enum';
import { ConfigService } from './config.service';
import { LanguageService } from './language.service';
import { texts } from './translations-formio';

@Injectable({
	providedIn: 'root'
})
export class UtilsService {

	private readonly FORMIO_TRANSLATIONS_ID: string = 'formioTranslations';

	private readonly headers: { key: string, value: string }[] = [
		{ key: "Content-Type", value: "application/json" },
		{ key: "Authorization", value: "Bearer @@userToken@@" },
		{ key: "contract-id", value: "@@contratoActual@@" },
		{ key: "schema-id", value: "@@currentSchema@@" },
		{ key: "workspace-id", value: "@@currentWorkspace@@" }
	];

	constructor(
		private readonly config: ConfigService,
		private readonly language: LanguageService
	) { }

	/**
	 * Summary. Replace custom variables of the schema based on regular expression
	 * 
	 * @search @@some_text@@
	 * 
	 * @param {string} schema formio schema to be parsed
	 * 
	 * @returns {object} JSON object with parsed schema
	 */
	public async customSchemaChanges(schema: string): Promise<FormioForm> {
		const headerString = JSON.stringify(this.headers);
		const expression = /(?<headerKey>"headers"\: )\[(?<headerValues>[^\]]*)\]/sg;
		schema = schema.toString().replace(expression, `"headers": [${headerString.slice(1, headerString.length - 1)},$<headerValues>]`);

		let search = "@@apiUrl@@";
		let searchRegExpr = new RegExp(search, 'g');
		schema = schema.replace(searchRegExpr, environment.apiUrlFormio);

		search = ",\n                            \"ignoreCache\": true\n";
		searchRegExpr = new RegExp(search, 'g');
		schema = schema.replace(searchRegExpr, "");

		search = ",\n                            \"ignoreCache\": false\n";
		searchRegExpr = new RegExp(search, 'g');
		schema = schema.replace(searchRegExpr, "");

		search = "\"selectValues\": \"entities\",";
		searchRegExpr = new RegExp(search, 'g');
		schema = schema.replace(searchRegExpr, "\"selectValues\": \"entities\",\n                            \"ignoreCache\": true,\n");

		return JSON.parse(schema);
	}

	/**
	 * Generates a plain-text file from the given content and triggers
	 * its download in the browser.
	 * @param {string} content - Text content to write into the file.
	 * @param {string} fileName - Name of the downloaded file (including extension).
	 * @returns {void}
	 */
	public downloadTextFile(content: string, fileName: string): void {
		const blob: Blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
		const url: string = URL.createObjectURL(blob);
		const link: HTMLAnchorElement = document.createElement('a');

		link.href = url;
		link.download = fileName;
		link.click();

		setTimeout(() => URL.revokeObjectURL(url), 0);
	}

	/**
	 * Parses file info to a link
	 * @param file file information
	 * @returns url to the file
	 */
	public getFileUrl(file: ItemWorkflowFile): string {
		switch (file.type) {
			case ItemWorkflowFileType.GDOC:
				return `${ItemWorkflowFilePrefixes.GDOC}${file.documentId}`;
			default:
				return ItemWorkflowFilePrefixes.DEFAULT;
		}
	}

	/**
	 * Merges the Formio i18n translations and current language into the
	 * given render options object and returns it.
	 * @param {any} renderOptions - Formio render options to enrich.
	 * @returns {Promise<any>} The render options with translations + language applied.
	 */
	public async renderFormioOptions(renderOptions: any): Promise<any> {
		let newTranslations: any = texts;

		if (newTranslations)
			for (const key in renderOptions.i18n)
				if (Object.prototype.hasOwnProperty.call(renderOptions.i18n, key))
					renderOptions.i18n[key] = { ...renderOptions.i18n[key], ...newTranslations[key] };

		renderOptions.language = this.language.language;

		return renderOptions;
	}
}
